import React from 'react';
import { LoadingComponent } from '../General/LoadingComponent';
import { useEmployeesPageContext } from './contexts/EmployeesPageContext';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import { DataTable } from '@/library/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { Employee } from '@/types/Employee/Employee';
import { ActionsMenu } from '@/library/ActionMenu';
import { useCrudPageStateContext } from '@/context/CrudPageContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DurationFormatter } from '../General/DurationFormatter';

function EmployeesTable() {
  const { t } = useTranslation();
  const { employees, employeesIsLoading } = useEmployeesPageContext();
  const { setOpenDeleteDialog, setEntityToDelete } = useCrudPageStateContext();
  const navigate = useNavigate();
  const selectedAffiliate = useSelector((state: any) => state.affiliate.selectedAffiliate);
  const isAdmin = selectedAffiliate.isAdmin;

  const columns: ColumnDef<Employee>[] = [
    {
      accessorKey: 'name',
      header: t('general.table_headers.name'),
      cell: ({ row }) => <div>{row.getValue('name')}</div>,
    },
    {
      accessorKey: 'weekly_hours',
      header: t('general.table_headers.hours'),
      cell: ({ row }) => (
        <div>
          {row.getValue('weekly_hours')} <span className="text-gray-500">h</span>
        </div>
      ),
    },
    {
      accessorKey: 'vacation_days',
      header: t('general.table_headers.holiday'),
      cell: ({ row }) => (
        <div>
          {row.getValue('vacation_days') || 0} <span className="text-gray-500">T</span>
        </div>
      ),
    },
    {
      accessorKey: 'saldo',
      header: t('general.table_headers.saldo'),
      cell: ({ row }) => {
        const saldo = row.original.saldo;
        return (
          <span className={saldo.amount >= 0 ? 'text-primary' : 'text-error'}>
            <DurationFormatter duration={saldo.amount} />
          </span>
        );
      },
    },
    {
      accessorKey: 'start_date',
      header: t('general.table_headers.start'),
      cell: ({ row }) => (
        <div>
          {row.original.start_date
            ? DateTime.fromISO(row.original.start_date).toFormat('dd.LL.y')
            : '-'}
        </div>
      ),
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <ActionsMenu
          entity={row.original}
          actions={[
            {
              label: t('general.edit'),
              onClick: () => {
                navigate('/affiliate/employee/' + row.original.id);
              },
            },
            {
              label: t('general.delete'),
              onClick: () => {
                setEntityToDelete(row.original);
                setOpenDeleteDialog(true);
              },
            },
          ]}
          isAdmin={isAdmin}
        />
      ),
    },
  ].filter(Boolean);

  return (
    <div>
      {employeesIsLoading ? (
        <LoadingComponent paddingTop={true} />
      ) : (
        <DataTable
          data={Object.values(employees)}
          columns={columns}
          hiddenColumnsOnMobile={['start_date', 'weekly_hours']}
        />
      )}
    </div>
  );
}

export { EmployeesTable };
